import { IComponent, ICustomEventData } from '@modules/src/interfaces/IComponents'
import { ContainersNames } from '../../containers/ContainersNames'
import { AssetType } from '@modules/src/enums/AssetType'
import { Orientation } from '@modules/src/enums/Orientation'
import { ComponentEvent } from '@modules/src/enums/ComponentEvent'
import { ModuleSignals } from '@modules/src/signals/ModuleSignals'
import GameSignals from '@/signals/GameSignals'
import { isFreeSpins } from '@/managers/ui/BackgroundManager'
import Facade from '@/Facade'
import * as PIXI from 'pixi.js'
import { ScalingTextField } from '@/containers/common/ScalingTextField'
import { JACKPOT_LADDER_NUMBER_STYLE, JACKPOT_LADDER_STEP_PRIZE_STYLE } from '@/containers/common/TextStyles'
import { SpineAnimation } from '@base/spineAnimation/SpineAnimation'
import { toCurrency } from '@/ui/currency'
import { toMajorUnits } from '@/utils/CurrencyUnits'
import { isHoldAndWinInsideFreeSpins } from '@/states/freespins/FreeSpinsFinalWin'
import { isSocialCasinoLocale } from '@/utils/Locale'

const JACKPOT_LADDER_COINS_COUNT = 11
const JACKPOT_LADDER_SPINE_IDLE_NAME = 'Jackpot Ladder Spine Idle'
const JACKPOT_LADDER_SPINE_ELECTRICITY_NAME = 'Jackpot Ladder Spine Electricity'
const JACKPOT_LADDER_SPINE_COIN_STEPS_NAME = 'Jackpot Ladder Spine Coin Steps'
const JACKPOT_LADDER_SPINE_COIN_GLOW_NAME = 'Jackpot Ladder Spine Coin Glow'
const JACKPOT_LADDER_CURRENT_JACKPOT_NAME = 'Jackpot Ladder Current Jackpot Label'
const JACKPOT_LADDER_DIMMED_ALPHA = 0.35
const JACKPOT_LADDER_ACTIVE_ALPHA = 1
const JACKPOT_LADDER_LEVEL_ACTIVE_TINT = 0xffffff
const JACKPOT_LADDER_LEVEL_DIMMED_TINT = 0x6f6f6f
const JACKPOT_LADDER_COIN_ACTIVE_TINT = 0xffffff
const JACKPOT_LADDER_COIN_DIMMED_TINT = 0x808080

const JACKPOT_LADDER_ELECTRICITY_TRANSFORMS: Array<{ x: number; y: number; scaleX: number; scaleY: number }> = [
    { x: 263, y: 927.5, scaleX: 0.475, scaleY: 0.95 },
    { x: 278.5, y: 841.5, scaleX: 0.575, scaleY: 0.95 },
    { x: 271.5, y: 760, scaleX: 0.575, scaleY: 0.95 },
    { x: 268, y: 675.5, scaleX: 0.575, scaleY: 0.95 },
    { x: 261, y: 590, scaleX: 0.59, scaleY: 0.95 },
    { x: 261, y: 507, scaleX: 0.61, scaleY: 0.95 },
    { x: 261, y: 426, scaleX: 0.63, scaleY: 0.95 },
    { x: 258.5, y: 340.5, scaleX: 0.69, scaleY: 0.95 },
    { x: 258.5, y: 256, scaleX: 0.78, scaleY: 0.95 },
    { x: 258.5, y: 165, scaleX: 0.85, scaleY: 0.95 },
    { x: 258.5, y: 79.5, scaleX: 0.93, scaleY: 0.95 },
]

const JACKPOT_LADDER_COIN_STEPS_TRANSFORMS: Array<{ x: number; y: number; scaleX: number; scaleY: number }> = [
    { x: 380, y: 931, scaleX: 0.85, scaleY: 0.85 },
    { x: 382, y: 840, scaleX: 0.85, scaleY: 0.85 },
    { x: 391.5, y: 757, scaleX: 0.85, scaleY: 0.85 },
    { x: 391.5, y: 671.5, scaleX: 0.85, scaleY: 0.85 },
    { x: 394, y: 586, scaleX: 0.85, scaleY: 0.85 },
    { x: 399.5, y: 503.5, scaleX: 0.85, scaleY: 0.85 },
    { x: 407, y: 418.5, scaleX: 0.85, scaleY: 0.85 },
    { x: 413, y: 338, scaleX: 0.85, scaleY: 0.85 },
    { x: 429.5, y: 255.5, scaleX: 0.85, scaleY: 0.85 },
    { x: 444.5, y: 164, scaleX: 0.85, scaleY: 0.85 },
    { x: 458.5, y: 73, scaleX: 0.85, scaleY: 0.85 },
]

//## "CURRENT JACKPOT" reads "CURRENT PRIZE POOL" on social-casino builds, which needs its own art.
//## Ladder sprites are anchored top-left (the framework never reads the `anchor` setting), and both
//## files pad the glyphs by the same 1-2px from that corner: the 195x72 social-casino art is simply
//## the longer string running further right. So the tier coordinates below apply unchanged —
//## re-centring it would push the label left, onto the step amount.
const JACKPOT_LADDER_CURRENT_JACKPOT_SRC = isSocialCasinoLocale()
    ? 'ladder_curent-jackpot_text_ss.png'
    : 'current_jackpot.png'

//## Custom Y positions for the current jackpot label, which replaces the coin at the milestone step.
const JACKPOT_LADDER_CURRENT_JACKPOT_TIER_Y: Record<number, number> = {
    7: 302, // Mini
    8: 220, // Minor
    9: 129, // Major
    10: 46, // Grand
}

const JACKPOT_LADDER_CURRENT_JACKPOT_POSITIONS: Array<{ x: number; y: number } | null> = [
    null,
    { x: 340.5, y: 805 },
    { x: 340.5, y: 725 },
    { x: 350, y: 639 },
    { x: 350, y: 555 },
    { x: 362.5, y: 470.5 },
    { x: 370, y: 385 },
]

let jackpotLadderContainerRef: PIXI.Container | null = null
let isLadderVisibilityBlockedByFeatureIntro = true
const FS_RUNTIME_LADDER_MILESTONES_KEY = 'fsRuntimeLadderMilestones'
const FS_RUNTIME_LADDER_COUNT_KEY = 'fsRuntimeLadderCount'

const getRuntimeLadderMilestones = (): number[] => {
    const runtimeSource = (Facade as any)?.[FS_RUNTIME_LADDER_MILESTONES_KEY]
    if (!Array.isArray(runtimeSource)) {
        return []
    }

    return runtimeSource.map((value: any) => Number(value)).filter((value: number) => Number.isFinite(value))
}

const setRuntimeLadderMilestones = (milestones: number[]): void => {
    ;(Facade as any)[FS_RUNTIME_LADDER_MILESTONES_KEY] = milestones.slice()
}

const getRuntimeLadderCount = (): number => Number((Facade as any)?.[FS_RUNTIME_LADDER_COUNT_KEY] ?? -1)

const setRuntimeLadderCount = (count: number): void => {
    ;(Facade as any)[FS_RUNTIME_LADDER_COUNT_KEY] = Number.isFinite(count) ? count : -1
}

const getLadderCount = (signalData?: any): number => {
    const signalLadderInfo = signalData?.other_feature_win_details?.ladder_info ?? signalData?.extra?.ladder_info
    const signalBonus = Array.isArray(signalData?.current_round?.bonus_games_won)
        ? signalData.current_round.bonus_games_won.find((bonus: any) => {
              const type = String(bonus?.type || '').toLowerCase()
              return type === 'freespins' || type === 'freespin'
          })
        : null
    const modelExtra = Facade.model?.spinData?.hold_and_win_extra as any

    return Number(
        signalData?.ladder_count ??
            signalLadderInfo?.ladder_count ??
            signalBonus?.ladder_count ??
            modelExtra?.ladder_count ??
            -1
    )
}

const isCurrentRoundRespin = (): boolean => {
    const spinType = String(Facade.model?.spinData?.spin_type || '').toLowerCase()
    return spinType === 'respin' || spinType === 'respins'
}

const logLadderVisibilityDebug = (
    tag: string,
    data?: ICustomEventData,
    extra?: Record<string, unknown>
): void => {
    void tag
    void data
    void extra
}

const JACKPOT_LADDER_COIN_TEXT_POSITIONS: Array<{ x: number; y: number }> = [
    { x: 383, y: 929.5 },
    { x: 385.5, y: 844 },
    { x: 392, y: 758 },
    { x: 392.5, y: 672.5 },
    { x: 396.5, y: 589.5 },
    { x: 400.5, y: 506.5 },
    { x: 406.5, y: 421 },
    { x: 415, y: 340 },
    { x: 432.5, y: 255.5 },
    { x: 445, y: 167.5 },
    { x: 459, y: 78 },
]

// Same step-prize multipliers used in lonestarlonghornholdandwin (bottom -> top).
const JACKPOT_LADDER_STEP_PRIZE_MULTIPLIERS: number[] = [1, 2, 5, 10, 15, 25, 35, 50, 100, 250, 1000]

//## Jackpot Ladder Levels Names.
export const JACKPOT_LADDER_LEVEL_NAMES: Record<number, string> = {
    7: 'Mini',
    8: 'Minor',
    9: 'Major',
    10: 'Grand',
}
const JACKPOT_LADDER_STEP_VALUE_POSITIONS: Array<{ x: number; y: number }> = [
    { x: 266.5, y: 917 },
    { x: 270, y: 830 },
    { x: 260.5, y: 747 },
    { x: 258.5, y: 662 },
    { x: 255.5, y: 577 },
    { x: 254.5, y: 493 },
    { x: 253.5, y: 406 },
    { x: 252.5, y: 325 },
    { x: 252.5, y: 240 },
    { x: 253.5, y: 150 },
    { x: 255, y: 65 },
]

//## Maxwidth for the step prize text fields (bottom -> top). The text fields are scaled down if the text exceeds the max width.
const JACKPOT_LADDER_STEP_PRIZE_MAX_WIDTHS: number[] = [
    140, // 0  (bottom)
    140, // 1
    160, // 2
    170, // 3
    180, // 4
    190, // 5
    200, // 6
    210, // 7  Mini
    220, // 8  Minor
    230, // 9  Major
    250, // 10 Grand (top)
]

const JACKPOT_LADDER_STEP_PRIZE_MAX_WIDTH_DEFAULT = 140
const JACKPOT_LADDER_STEP_PRIZE_MIN_SCALE = 0.9

const STEP_PRIZE_NUMBER_FORMAT = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
})

const findChildByName = <T extends PIXI.Container = PIXI.Container>(
    container: PIXI.Container | null,
    name: string
): T | null => {
    if (!container) {
        return null
    }

    if ((container as any).name === name || (container as any).label === name) {
        return container as T
    }

    for (const child of container.children) {
        const match = findChildByName<T>(child as PIXI.Container, name)
        if (match) {
            return match
        }
    }

    return null
}

const stopSpine = (spine: SpineAnimation | null): void => {
    if (!spine) {
        return
    }

    try {
        spine.stop()
    } catch {
        // ignore stop errors during state sync
    }
}

const getCurrentTargetLevelIndex = (milestones: number[]): number => {
    for (let index = 0; index < milestones.length; index++) {
        const milestone = milestones[index]
        if (Number.isFinite(milestone) && milestone > 0) {
            return index
        }
    }

    return -1
}

const applyCoinLevelVisualState = (milestones: number[]): void => {
    const activeLevelIndex = getCurrentTargetLevelIndex(milestones)
    const currentJackpotIndex = activeLevelIndex > 0 ? activeLevelIndex - 1 : -1
    const currentJackpotSprite = findChildByName<PIXI.Container>(
        jackpotLadderContainerRef,
        JACKPOT_LADDER_CURRENT_JACKPOT_NAME
    )

    if (currentJackpotSprite) {
        currentJackpotSprite.visible = false
    }

    for (let index = 0; index < JACKPOT_LADDER_COINS_COUNT; index++) {
        const milestoneValue = Number(milestones[index] ?? 0)
        const isCurrentJackpotLevel = index === currentJackpotIndex
        const isDimmedLevel = (!Number.isFinite(milestoneValue) || milestoneValue <= 0) && !isCurrentJackpotLevel
        const levelTint = isDimmedLevel ? JACKPOT_LADDER_LEVEL_DIMMED_TINT : JACKPOT_LADDER_LEVEL_ACTIVE_TINT
        const coinTint = isDimmedLevel ? JACKPOT_LADDER_COIN_DIMMED_TINT : JACKPOT_LADDER_COIN_ACTIVE_TINT
        const prizeAlpha = isDimmedLevel ? JACKPOT_LADDER_DIMMED_ALPHA : JACKPOT_LADDER_ACTIVE_ALPHA

        const levelSprite = findChildByName<PIXI.Container>(jackpotLadderContainerRef, `Jackpot Ladder Level ${index}`)
        if (levelSprite) {
            ;(levelSprite as any).tint = levelTint
            levelSprite.alpha = JACKPOT_LADDER_ACTIVE_ALPHA
        }

        const jackpotLevelName = JACKPOT_LADDER_LEVEL_NAMES[index]
        const jackpotLabel = jackpotLevelName
            ? findChildByName<PIXI.Container>(jackpotLadderContainerRef, `Jackpot Ladder ${jackpotLevelName} Label`)
            : null
        if (jackpotLabel) {
            jackpotLabel.visible = index > currentJackpotIndex
        }

        const coinSprite = findChildByName<PIXI.Container>(jackpotLadderContainerRef, `Jackpot Ladder Coin ${index + 1}`)
        if (coinSprite) {
            ;(coinSprite as any).tint = coinTint
            coinSprite.alpha = JACKPOT_LADDER_ACTIVE_ALPHA
            coinSprite.visible = !isCurrentJackpotLevel

            if (isCurrentJackpotLevel && currentJackpotSprite) {
                currentJackpotSprite.visible = true
                //## Tier Y overrides for the current jackpot label, which replaces the coin at the milestone step.
                const tierY = JACKPOT_LADDER_CURRENT_JACKPOT_TIER_Y[index]
                const currentJackpotPosition = JACKPOT_LADDER_CURRENT_JACKPOT_POSITIONS[index]
                if (currentJackpotPosition) {
                    currentJackpotSprite.position.set(
                        currentJackpotPosition.x,
                        tierY ?? currentJackpotPosition.y
                    )
                } else {
                    const currentJackpotY = index === 0 ? 895 : coinSprite.y
                    currentJackpotSprite.position.set(coinSprite.x, tierY ?? currentJackpotY)
                }
            }
        }

        const coinValueText = findChildByName<PIXI.Container>(jackpotLadderContainerRef, `Jackpot Ladder Coin Value ${index + 1}`)
        if (coinValueText) {
            ;(coinValueText as any).tint = coinTint
            coinValueText.alpha = JACKPOT_LADDER_ACTIVE_ALPHA
            coinValueText.visible = !isCurrentJackpotLevel
        }

        // Step prizes: dim with alpha only on the container.
        // Do not tint BitmapText (and never tint parent+content — multiply makes text invisible).
        const stepPrizeText = findChildByName<any>(jackpotLadderContainerRef, `Jackpot Ladder Step Prize ${index + 1}`)
        if (stepPrizeText) {
            stepPrizeText.visible = true
            stepPrizeText.tint = JACKPOT_LADDER_LEVEL_ACTIVE_TINT
            stepPrizeText.alpha = prizeAlpha
            if ('content' in stepPrizeText && stepPrizeText.content) {
                stepPrizeText.content.tint = JACKPOT_LADDER_LEVEL_ACTIVE_TINT
                stepPrizeText.content.alpha = JACKPOT_LADDER_ACTIVE_ALPHA
            }
        }
    }
}

/** Re-apply step prize / coin counter texts after FS↔H&W visibility transitions. */
const refreshLadderTextValues = (allowPendingHoldAndWin: boolean = false): void => {
    if (!jackpotLadderContainerRef) {
        return
    }

    //## Second gate on the same rule as the container's. When the retrigger path shows the ladder
    //## with a Hold & Win already pending, this has to be lifted too — otherwise the frame renders
    //## with every step prize blanked to ''.
    const canShow =
        (allowPendingHoldAndWin || shouldShowLaddersInFreeSpins()) && !isLadderVisibilityBlockedByFeatureIntro
    const milestones = getLadderMilestones()
    const activeLevelIndex = getCurrentTargetLevelIndex(milestones)
    const currentJackpotIndex = activeLevelIndex > 0 ? activeLevelIndex - 1 : -1

    for (let index = 0; index < JACKPOT_LADDER_COINS_COUNT; index++) {
        const stepPrizeText = findChildByName<any>(jackpotLadderContainerRef, `Jackpot Ladder Step Prize ${index + 1}`)
        if (stepPrizeText) {
            if (!canShow) {
                stepPrizeText.text = ''
            } else {
                const totalBet = getCurrentTotalBet()
                const multiplier = Number(JACKPOT_LADDER_STEP_PRIZE_MULTIPLIERS[index] ?? 0)
                stepPrizeText.visible = true
                stepPrizeText.tint = JACKPOT_LADDER_LEVEL_ACTIVE_TINT
                stepPrizeText.alpha = JACKPOT_LADDER_ACTIVE_ALPHA
                if ('content' in stepPrizeText && stepPrizeText.content) {
                    stepPrizeText.content.tint = JACKPOT_LADDER_LEVEL_ACTIVE_TINT
                    stepPrizeText.content.alpha = JACKPOT_LADDER_ACTIVE_ALPHA
                }
                stepPrizeText.text = formatStepPrizeValue(totalBet * multiplier)
            }
        }

        const coinValueText = findChildByName<any>(jackpotLadderContainerRef, `Jackpot Ladder Coin Value ${index + 1}`)
        if (!coinValueText) {
            continue
        }

        if (!canShow) {
            coinValueText.text = ''
            continue
        }

        const value = milestones[index]
        const isCurrentJackpotLevel = index === currentJackpotIndex
        if (!Number.isFinite(value) || value < 0 || (value === 0 && isCurrentJackpotLevel)) {
            coinValueText.text = ''
            continue
        }

        coinValueText.visible = !isCurrentJackpotLevel
        coinValueText.alpha = JACKPOT_LADDER_ACTIVE_ALPHA
        coinValueText.text = String(Math.trunc(value))
    }
}

const applyActiveLevelEffects = (milestones: number[]): void => {
    const electricitySpine = findChildByName<SpineAnimation>(
        jackpotLadderContainerRef,
        JACKPOT_LADDER_SPINE_ELECTRICITY_NAME
    )
    const glowSpine = findChildByName<SpineAnimation>(jackpotLadderContainerRef, JACKPOT_LADDER_SPINE_COIN_STEPS_NAME)
    const currentTargetIndex = getCurrentTargetLevelIndex(milestones)

    const electricityTransform = JACKPOT_LADDER_ELECTRICITY_TRANSFORMS[currentTargetIndex]
    const coinStepTransform = JACKPOT_LADDER_COIN_STEPS_TRANSFORMS[currentTargetIndex]

    if (currentTargetIndex < 0 || !electricityTransform || !coinStepTransform) {
        if (electricitySpine) {
            stopSpine(electricitySpine)
            electricitySpine.visible = false
        }

        if (glowSpine) {
            stopSpine(glowSpine)
            glowSpine.visible = false
        }

        const coinGlowSpineHide = findChildByName<SpineAnimation>(jackpotLadderContainerRef, JACKPOT_LADDER_SPINE_COIN_GLOW_NAME)
        if (coinGlowSpineHide) {
            stopSpine(coinGlowSpineHide)
            coinGlowSpineHide.visible = false
        }

        return
    }

    if (electricitySpine) {
        electricitySpine.visible = true
        electricitySpine.position.set(electricityTransform.x, electricityTransform.y)
        electricitySpine.scale.set(electricityTransform.scaleX, electricityTransform.scaleY)
        stopSpine(electricitySpine)
        electricitySpine.start(['electricity'], [true], 0, [1])
    }

    if (glowSpine) {
        // State 2 – One Coin Away: only 1 coin left to reach the next milestone → stronger glow (coin_step2, loop)
        // State 1 – Collecting: any other collect state → subtle glow (coin_step1, loop)
        const remainingCoins = Number(milestones[currentTargetIndex] ?? 0)
        const isOneCoinAway = Number.isFinite(remainingCoins) && remainingCoins === 1
        const animName = isOneCoinAway ? 'coin_step2' : 'coin_step1'

        glowSpine.visible = true
        glowSpine.position.set(coinStepTransform.x, coinStepTransform.y)
        glowSpine.scale.set(coinStepTransform.scaleX, coinStepTransform.scaleY)
        stopSpine(glowSpine)
        glowSpine.start([animName], [true], 0, [1])
    }

    const coinGlowSpine = findChildByName<SpineAnimation>(jackpotLadderContainerRef, JACKPOT_LADDER_SPINE_COIN_GLOW_NAME)
    if (coinGlowSpine) {
        coinGlowSpine.visible = true
        coinGlowSpine.position.set(coinStepTransform.x, coinStepTransform.y)
        coinGlowSpine.scale.set(coinStepTransform.scaleX, coinStepTransform.scaleY)
        stopSpine(coinGlowSpine)
        coinGlowSpine.start(['coin_step3'], [true], 0, [1])
    }
}

const refreshLadderVisualState = (data?: ICustomEventData, allowPendingHoldAndWin: boolean = false): void => {
    if (!jackpotLadderContainerRef) {
        return
    }

    const milestones = getLadderMilestones(data?.signalData)
    refreshLadderTextValues(allowPendingHoldAndWin)
    applyCoinLevelVisualState(milestones)

    const idleSpine = findChildByName<SpineAnimation>(jackpotLadderContainerRef, JACKPOT_LADDER_SPINE_IDLE_NAME)
    if (idleSpine) {
        idleSpine.visible = true
        stopSpine(idleSpine)
        idleSpine.start(['ladder_idle'], [true], 0, [1])
    }

    applyActiveLevelEffects(milestones)
}

const getLadderMilestones = (signalData?: any): number[] => {
    const milestonesFromDirectSignal = signalData?.unlock_milestones_count
    const milestonesFromSignal = signalData?.hold_and_win_extra?.ladder_unlock_milestones_count
    const milestonesFromTriggeredBonus = Array.isArray(signalData?.current_round?.bonus_games_won)
        ? signalData.current_round.bonus_games_won.find((bonus: any) => {
              const type = String(bonus?.type || '').toLowerCase()
              return type === 'freespins' || type === 'freespin'
          })?.unlock_milestones_count
        : []
    const milestonesFromModel = Facade.model?.spinData?.hold_and_win_extra?.ladder_unlock_milestones_count
    const milestonesFromNextRound = (() => {
        const nextRound = signalData?.next_round || (Facade.model?.spinData as any)?.next_round
        const fromNext =
            nextRound?.unlock_milestones_count ??
            nextRound?.ladder_info?.unlock_milestones_count ??
            nextRound?.other_feature_win_details?.ladder_info?.unlock_milestones_count
        return Array.isArray(fromNext) ? fromNext : []
    })()
    const source =
        Array.isArray(milestonesFromDirectSignal) && milestonesFromDirectSignal.length
            ? milestonesFromDirectSignal
            : Array.isArray(milestonesFromSignal) && milestonesFromSignal.length
            ? milestonesFromSignal
            : Array.isArray(milestonesFromTriggeredBonus) && milestonesFromTriggeredBonus.length
              ? milestonesFromTriggeredBonus
            : Array.isArray(milestonesFromModel) && milestonesFromModel.length
              ? milestonesFromModel
            : Array.isArray(milestonesFromNextRound) && milestonesFromNextRound.length
              ? milestonesFromNextRound
            : Array.isArray(milestonesFromModel)
              ? milestonesFromModel
              : []

    const resolved = source.map((value: any) => Number(value)).filter((value: number) => Number.isFinite(value))
    const runtimeMilestones = getRuntimeLadderMilestones()
    const runtimeLadderCount = getRuntimeLadderCount()
    const resolvedLadderCount = getLadderCount(signalData)
    const isFsRound = Boolean(Facade.model?.roundState?.isFreeSpins)
    const isHoldAndWinActive = Boolean(Facade.model?.spinState?.isHoldAndWinActive)
    const shouldUseRuntimeFallback = isCurrentRoundRespin() || isHoldAndWinActive

    if (
        isFsRound &&
        !isHoldAndWinActive &&
        Boolean((Facade as any).isFsCollectPresentationActive) &&
        runtimeMilestones.length
    ) {
        return runtimeMilestones
    }

    if (
        isFsRound &&
        !isHoldAndWinActive &&
        runtimeMilestones.length &&
        runtimeLadderCount >= 0 &&
        resolvedLadderCount > runtimeLadderCount
    ) {
        return runtimeMilestones
    }

    if (shouldUseRuntimeFallback && runtimeMilestones.length) {
        return runtimeMilestones
    }

    if (resolved.length) {
        const isSourceFromTriggeredBonus =
            Array.isArray(milestonesFromTriggeredBonus) && milestonesFromTriggeredBonus.length > 0 && source === milestonesFromTriggeredBonus
        if (isSourceFromTriggeredBonus || (isFsRound && !isHoldAndWinActive)) {
            setRuntimeLadderMilestones(resolved)
            setRuntimeLadderCount(resolvedLadderCount)
        }

        return resolved
    }

    if (runtimeMilestones.length) {
        return runtimeMilestones
    }

    return []
}

export const getJackpotLadderCollectTarget = (): PIXI.Container | null => {
    if (!jackpotLadderContainerRef) {
        return null
    }

    const milestones = getLadderMilestones()
    const activeIndex = getCurrentTargetLevelIndex(milestones)

    if (activeIndex < 0) {
        return null
    }

    const numberTarget = findChildByName<PIXI.Container>(
        jackpotLadderContainerRef,
        `Jackpot Ladder Coin Value ${activeIndex + 1}`
    )
    if (numberTarget) {
        return numberTarget
    }

    const coinTarget = findChildByName<PIXI.Container>(
        jackpotLadderContainerRef,
        `Jackpot Ladder Coin ${activeIndex + 1}`
    )
    if (coinTarget) {
        return coinTarget
    }

    return findChildByName<PIXI.Container>(jackpotLadderContainerRef, JACKPOT_LADDER_SPINE_COIN_STEPS_NAME)
}

const updateCoinValue = (data: ICustomEventData, coinIndex: number): void => {
    const element = data.component.componentElement
    const milestones = getLadderMilestones(data.signalData)
    const value = milestones[coinIndex]

    if (!canShowLadders(data) || !Number.isFinite(value)) {
        element.text = ''
        return
    }

    const activeLevelIndex = getCurrentTargetLevelIndex(milestones)
    const currentJackpotIndex = activeLevelIndex > 0 ? activeLevelIndex - 1 : -1
    const isCurrentJackpotLevel = coinIndex === currentJackpotIndex

    if (value < 0 || (value === 0 && isCurrentJackpotLevel)) {
        element.text = ''
        return
    }

    element.visible = true
    element.alpha = 1
    element.text = String(Math.trunc(value))
}

const clearCoinValue = (data: ICustomEventData): void => {
    const element = data.component.componentElement
    element.text = ''
}

const getCurrentTotalBet = (): number => {
    const totalBet = Number(Facade.model?.currentBaseBet ?? Facade.model?.currentBet ?? 0)
    return Number.isFinite(totalBet) && totalBet > 0 ? totalBet : 0
}

const getCurrencySymbol = (): string => {
    const rawCurrency = String(Facade.model?.initData?.player?.currency ?? '')
    return toCurrency(rawCurrency)
}

const formatStepPrizeValue = (value: number): string => {
    if (!Number.isFinite(value) || value <= 0) {
        return ''
    }

    const roundedMinorUnits = Math.round(value)
    const majorUnits = toMajorUnits(roundedMinorUnits)
    const currencySymbol = getCurrencySymbol()

    return `${currencySymbol}${STEP_PRIZE_NUMBER_FORMAT.format(majorUnits)}`
}

const updateStepPrizeValue = (data: ICustomEventData, stepIndex: number): void => {
    const element = data.component.componentElement
    const signalName = (data as any)?.signalName

    // holdAndWinStop can fire before FS flags settle; syncLaddersForFeatureStates
    // restores texts on the next tick via refreshLadderVisualState.
    if (!canShowLadders(data)) {
        if (signalName !== (GameSignals as any).holdAndWinStop) {
            element.text = ''
        }
        return
    }

    const totalBet = getCurrentTotalBet()
    const multiplier = Number(JACKPOT_LADDER_STEP_PRIZE_MULTIPLIERS[stepIndex] ?? 0)
    const stepPrize = totalBet * multiplier

    element.visible = true
    element.text = formatStepPrizeValue(stepPrize)

    const milestones = getLadderMilestones(data?.signalData)
    const activeLevelIndex = getCurrentTargetLevelIndex(milestones)
    const currentJackpotIndex = activeLevelIndex > 0 ? activeLevelIndex - 1 : -1
    const milestoneValue = Number(milestones[stepIndex] ?? 0)
    const isCurrentJackpotLevel = stepIndex === currentJackpotIndex
    const isDimmedLevel = (!Number.isFinite(milestoneValue) || milestoneValue <= 0) && !isCurrentJackpotLevel
    const alpha = isDimmedLevel ? JACKPOT_LADDER_DIMMED_ALPHA : JACKPOT_LADDER_ACTIVE_ALPHA

    element.tint = JACKPOT_LADDER_LEVEL_ACTIVE_TINT
    element.alpha = alpha
    if ('content' in element && element.content) {
        element.content.tint = JACKPOT_LADDER_LEVEL_ACTIVE_TINT
        element.content.alpha = JACKPOT_LADDER_ACTIVE_ALPHA
    }
}

const clearStepPrizeValue = (data: ICustomEventData): void => {
    const element = data.component.componentElement
    element.text = ''
}

const createCoinValueItem = (coinNumber: number, x: number, y: number): any => ({
    name: `Jackpot Ladder Coin Value ${coinNumber}`,
    type: AssetType.CUSTOM,
    anchor: 0.5,
    position: [
        { orientation: Orientation.LANDSCAPE, x, y },
        { orientation: Orientation.PORTRAIT, x, y },
    ],
    elementFactory: () => new ScalingTextField(0.72, 105, PIXI.BitmapText as any, JACKPOT_LADDER_NUMBER_STYLE),
    events: [
        {
            onGameSignalsTriggered: [
                GameSignals.showFreeSpinsBackground,
                (GameSignals as any).featureIntroSkipConfirmed,
                GameSignals.bonusStartComplete,
                GameSignals.completeTransitionAnimation,
                GameSignals.restoreFreeSpins,
                GameSignals.showFreeSpinsRetrigger,
                GameSignals.updateJarValue,
                GameSignals.multiplierLanded,
                (GameSignals as any).holdAndWinStop,
            ],
            event: ComponentEvent.CUSTOM_NEW,
            callback: (data: ICustomEventData) => updateCoinValue(data, coinNumber - 1),
        },
        {
            onGameSignalsTriggered: [
                (GameSignals as any).featureIntroRequested,
                (GameSignals as any).holdAndWinTransitionStart,
                (GameSignals as any).holdAndWinStartSpin,
                GameSignals.showBaseGameBackground,
                GameSignals.bonusStop,
            ],
            event: ComponentEvent.CUSTOM_NEW,
            callback: clearCoinValue,
        },
    ],
})

const createStepPrizeValueItem = (stepNumber: number, x: number, y: number): any => ({
    name: `Jackpot Ladder Step Prize ${stepNumber}`,
    type: AssetType.CUSTOM,
    anchor: 0.5,
    position: [
        { orientation: Orientation.LANDSCAPE, x, y },
        { orientation: Orientation.PORTRAIT, x, y },
    ],
    elementFactory: () =>
        new ScalingTextField(
            JACKPOT_LADDER_STEP_PRIZE_MIN_SCALE,
            JACKPOT_LADDER_STEP_PRIZE_MAX_WIDTHS[stepNumber - 1] ?? JACKPOT_LADDER_STEP_PRIZE_MAX_WIDTH_DEFAULT,
            PIXI.BitmapText as any,
            JACKPOT_LADDER_STEP_PRIZE_STYLE
        ),
    events: [
        {
            onGameSignalsTriggered: [
                (GameSignals as any).featureIntroSkipConfirmed,
                GameSignals.bonusStartComplete,
                GameSignals.completeTransitionAnimation,
                GameSignals.restoreFreeSpins,
                GameSignals.showFreeSpinsRetrigger,
                (GameSignals as any).holdAndWinStop,
                GameSignals.betHasChanged,
            ],
            event: ComponentEvent.CUSTOM_NEW,
            callback: (data: ICustomEventData) => updateStepPrizeValue(data, stepNumber - 1),
        },
        {
            onGameSignalsTriggered: [
                (GameSignals as any).featureIntroRequested,
                (GameSignals as any).holdAndWinTransitionStart,
                (GameSignals as any).holdAndWinStartSpin,
                GameSignals.showBaseGameBackground,
                GameSignals.bonusStop,
            ],
            event: ComponentEvent.CUSTOM_NEW,
            callback: clearStepPrizeValue,
        },
    ],
})

const blockLaddersVisibility = (data: ICustomEventData): void => {
    isLadderVisibilityBlockedByFeatureIntro = true
    logLadderVisibilityDebug('blockLaddersVisibility', data)
    hideLadders(data)
}

//## Latched from the featureIntroRequested payload so the featureIntroContinue handler can tell a
//## free spins retrigger close from a Hold & Win intro close — the signal itself carries no args.
let isFreeSpinsRetriggerIntro = false

const latchFeatureIntroKind = (data: ICustomEventData): void => {
    const payload: any = (data as any)?.signalData
    isFreeSpinsRetriggerIntro = Boolean(payload?.retrigger) && String(payload?.kind || '') === 'freespins'
}

/**
 * featureIntroContinue after a free spins retrigger.
 *
 * The pending-H&W guard below — it was added because an empty ladder overlaps the
 * respin counter on exactly this spin (FS retrigger + 6 coins). 
 * 
 */
const unblockLaddersAfterFeatureIntro = (data: ICustomEventData): void => {
    if (isFreeSpinsRetriggerIntro) {
        isFreeSpinsRetriggerIntro = false
        unblockLaddersVisibility(data, true)
        return
    }

    unblockLaddersVisibility(data)
}

const unblockLaddersVisibility = (data: ICustomEventData, allowPendingHoldAndWin: boolean = false): void => {
    //## A Hold&Win triggered on the same spin (FS retrigger + 6 coins) owns the screen next:
    //## unblocking here would leave the empty ladder overlapping the respin counter.
    if (!allowPendingHoldAndWin && isHoldAndWinPendingOrActive()) {
        blockLaddersVisibility(data)
        return
    }

    isLadderVisibilityBlockedByFeatureIntro = false
    const element = data.component.componentElement
    jackpotLadderContainerRef = element as PIXI.Container
    const hasLadderData = getLadderMilestones(data?.signalData).length > 0
    element.visible = hasLadderData
    element.alpha = hasLadderData ? 1 : 0
    if (!hasLadderData) {
        refreshLadderTextValues(allowPendingHoldAndWin)
        return
    }
    logLadderVisibilityDebug('unblockLaddersVisibility:beforeRefresh', data)
    refreshLadderVisualState(data, allowPendingHoldAndWin)
    logLadderVisibilityDebug('unblockLaddersVisibility:afterRefresh', data)
}

//## isHoldAndWinActive is only set once FinalState routes into the feature, so the respins
//## already pending on the current spin have to be taken into account as well.
const isHoldAndWinPendingOrActive = (): boolean => {
    if (Boolean(Facade.model?.spinState?.isHoldAndWinActive)) {
        return true
    }

    const spinData: any = Facade.model?.spinData || {}
    const nextRoundType = String(spinData?.next_round_type || '').toLowerCase()
    const respinsLeft = Number(spinData?.respins_left ?? 0)

    return (nextRoundType === 'respins' || nextRoundType === 'respin') && respinsLeft > 0
}

const shouldShowLaddersInFreeSpins = (): boolean => {
    const isFsRound = Boolean(
        Facade.model?.roundState?.isFreeSpins ||
            isFreeSpins() ||
            (Facade.model && isHoldAndWinInsideFreeSpins(Facade.model))
    )
    return isFsRound && !isHoldAndWinPendingOrActive() && getLadderMilestones().length > 0
}

const syncLaddersForFeatureStates = (data: ICustomEventData): void => {
    // Hold&Win stop can arrive before model flags settle on the same tick.
    window.setTimeout(() => {
        if (shouldShowLaddersInFreeSpins()) {
            unblockLaddersVisibility(data)
            return
        }

        blockLaddersVisibility(data)
    }, 0)
}

const canShowLadders = (data?: ICustomEventData): boolean => {
    const signalName = (data as any)?.signalName
    const shouldShowByCurrentState = shouldShowLaddersInFreeSpins()
    const isForceVisibleSignal =
        signalName === (GameSignals as any).featureIntroContinue ||
        signalName === GameSignals.restoreFreeSpins ||
        signalName === GameSignals.showFreeSpinsRetrigger

    // While blocked by feature intro, ladders must stay hidden unless an explicit
    // unblock/restore signal is handling the visibility transition.
    if (isLadderVisibilityBlockedByFeatureIntro) {
        if (!isForceVisibleSignal) {
            logLadderVisibilityDebug('canShowLadders:false(blocked)', data)
            return false
        }

        logLadderVisibilityDebug('canShowLadders:true(forceSignal)', data)
        return true
    }

    const canShow = shouldShowByCurrentState
    logLadderVisibilityDebug('canShowLadders:stateCheck', data, {
        canShow,
    })
    return canShow
}

const showLadders = (data: ICustomEventData): void => {
    const element = data.component.componentElement
    jackpotLadderContainerRef = element as PIXI.Container
    logLadderVisibilityDebug('showLadders:enter', data)

    if (!canShowLadders(data)) {
        element.visible = false
        element.alpha = 0
        logLadderVisibilityDebug('showLadders:blocked', data)
        return
    }

    element.visible = true
    element.alpha = 1
    refreshLadderVisualState(data)
    logLadderVisibilityDebug('showLadders:visible', data)
}

const hideLadders = (data: ICustomEventData): void => {
    const element = data.component.componentElement

    element.visible = false
    element.alpha = 0
    logLadderVisibilityDebug('hideLadders', data)

    const electricitySpine = findChildByName<SpineAnimation>(
        jackpotLadderContainerRef,
        JACKPOT_LADDER_SPINE_ELECTRICITY_NAME
    )
    const glowSpine = findChildByName<SpineAnimation>(jackpotLadderContainerRef, JACKPOT_LADDER_SPINE_COIN_STEPS_NAME)
    const coinGlowSpine = findChildByName<SpineAnimation>(jackpotLadderContainerRef, JACKPOT_LADDER_SPINE_COIN_GLOW_NAME)

    if (electricitySpine) {
        stopSpine(electricitySpine)
        electricitySpine.visible = false
    }

    if (glowSpine) {
        stopSpine(glowSpine)
        glowSpine.visible = false
    }

    if (coinGlowSpine) {
        stopSpine(coinGlowSpine)
        coinGlowSpine.visible = false
    }
}

export const JACKPOT_LADDERS_COMPONENT: IComponent = {
    name: ContainersNames.JACKPOT_LADDERS_CONTAINER,
    type: AssetType.CONTAINER,
    visible: false,
    zIndex: new Map<Orientation, number>([
        [Orientation.LANDSCAPE, 405],
        [Orientation.PORTRAIT, 405],
    ]),
    events: [
        {
            //## Baseline for milestone detection, taken once per session before any coin is
            //## collected. Retrigger is deliberately excluded: re-seeding mid-session would
            //## discard the progress the next collect needs to compare against.
            event: ComponentEvent.CUSTOM_NEW,
            onGameSignalsTriggered: [GameSignals.showFreeSpinsBackground],
            callback: (data: ICustomEventData) => {
                if (!Boolean(Facade.model?.isFreeSpinsRestore)) {
                    resetLadderSessionState()
                }

                seedLadderMilestoneTracking(data?.signalData)
            },
        },
        {
            event: ComponentEvent.CUSTOM_NEW,
            onGameSignalsTriggered: [GameSignals.restoreFreeSpins],
            callback: (data: ICustomEventData) => {
                seedLadderMilestoneTracking(data?.signalData)
            },
        },
        {
            event: ComponentEvent.CUSTOM_NEW,
            onGameSignalsTriggered: [GameSignals.completeSplashScreenState],
            callback: blockLaddersVisibility,
        },
        {
            event: ComponentEvent.CUSTOM_NEW,
            onGameSignalsTriggered: [(GameSignals as any).featureIntroRequested],
            callback: (data: ICustomEventData) => {
                latchFeatureIntroKind(data)
                blockLaddersVisibility(data)
            },
        },
        {
            event: ComponentEvent.CUSTOM_NEW,
            onGameSignalsTriggered: [GameSignals.showFreeSpinsBackground],
            callback: (data: ICustomEventData) => {
                logLadderVisibilityDebug('event:showFreeSpinsBackground', data)
                if (Boolean(Facade.model?.isFreeSpinsRestore)) {
                    // Restore FS flow must show ladder immediately.
                    unblockLaddersVisibility(data)
                    return
                }

                // FS trigger flow: keep hidden until explicit intro confirmation.
                blockLaddersVisibility(data)
            },
        },
        {
            event: ComponentEvent.CUSTOM_NEW,
            onGameSignalsTriggered: [(GameSignals as any).featureIntroContinue],
            callback: unblockLaddersAfterFeatureIntro,
        },
        {
            event: ComponentEvent.CUSTOM_NEW,
            onGameSignalsTriggered: [(GameSignals as any).holdAndWinStop],
            callback: syncLaddersForFeatureStates,
        },
        {
            event: ComponentEvent.CUSTOM_NEW,
            onGameSignalsTriggered: [(GameSignals as any).holdAndWinStartSpin],
            callback: blockLaddersVisibility,
        },
        {
            event: ComponentEvent.CUSTOM_NEW,
            onGameSignalsTriggered: [GameSignals.restoreFreeSpins, GameSignals.showFreeSpinsRetrigger],
            callback: unblockLaddersVisibility,
        },
        {
            event: ComponentEvent.CUSTOM_NEW,
            onGameSignalsTriggered: [
                (GameSignals as any).featureIntroSkipConfirmed,
                GameSignals.restoreFreeSpins,
                GameSignals.showFreeSpinsRetrigger,
                GameSignals.updateJarValue,
                GameSignals.multiplierLanded,
                (GameSignals as any).holdAndWinStop,
            ],
            callback: refreshLadderVisualState,
        },
        {
            event: ComponentEvent.CUSTOM_NEW,
            onGameSignalsTriggered: [ModuleSignals.gameResized],
            callback: (data: ICustomEventData) => {
                requestAnimationFrame(() => {
                    const element = data.component.componentElement
                    if (!element || element.destroyed) {
                        return
                    }

                    if (canShowLadders(data)) {
                        showLadders(data)
                        return
                    }

                    hideLadders(data)
                })
            },
        },
        {
            event: ComponentEvent.CUSTOM_NEW,
            onGameSignalsTriggered: [
                (GameSignals as any).holdAndWinTransitionStart,
                (GameSignals as any).holdAndWinStartSpin,
                GameSignals.showBaseGameBackground,
                GameSignals.bonusStop,
            ],
            callback: blockLaddersVisibility,
        },
    ],
    position: [
        {
            orientation: Orientation.LANDSCAPE,
            x: -49.5,
            y: 27,
        },
        {
            orientation: Orientation.PORTRAIT,
            x: 350.5,
            y: -10,
        },
    ],
    scale: [
        {
            orientation: Orientation.LANDSCAPE,
            x: 0.75,
            y: 0.75,
        },
        {
            orientation: Orientation.PORTRAIT,
            x: 0.62,
            y: 0.62,
        },
    ],
    items: [
        // {
        //     name: 'Jackpot Ladder Frame',
        //     type: AssetType.SPRITE,
        //     src: 'ladder_frames.png',
        //     anchor: 0.5,
        //     position: [
        //         { orientation: Orientation.LANDSCAPE, x: 0, y: 0 },
        //         { orientation: Orientation.PORTRAIT, x: 0, y: 0 },
        //     ],
        // },
        {
            name: 'Jackpot Ladder Level 0',
            type: AssetType.SPRITE,
            src: 'level_0',
            anchor: 0.5,
            position: [
                { orientation: Orientation.LANDSCAPE, x: 166, y: 893 },
                { orientation: Orientation.PORTRAIT, x: 166, y: 893 },
            ],
        },
        {
            name: 'Jackpot Ladder Level 1',
            type: AssetType.SPRITE,
            src: 'level_1',
            anchor: 0.5,
            position: [
                { orientation: Orientation.LANDSCAPE, x: 160.5, y: 806 },
                { orientation: Orientation.PORTRAIT, x: 160.5, y: 806 },
            ],
        },
        {
            name: 'Jackpot Ladder Level 2',
            type: AssetType.SPRITE,
            src: 'level_2',
            anchor: 0.5,
            position: [
                { orientation: Orientation.LANDSCAPE, x: 154.5, y: 723 },
                { orientation: Orientation.PORTRAIT, x: 154.5, y: 723 },
            ],
        },
        {
            name: 'Jackpot Ladder Level 3',
            type: AssetType.SPRITE,
            src: 'level_3',
            anchor: 0.5,
            position: [
                { orientation: Orientation.LANDSCAPE, x: 148.75, y: 638 },
                { orientation: Orientation.PORTRAIT, x: 148.75, y: 638 },
            ],
        },
        {
            name: 'Jackpot Ladder Level 4',
            type: AssetType.SPRITE,
            src: 'level_4',
            anchor: 0.5,
            position: [
                { orientation: Orientation.LANDSCAPE, x: 143, y: 553 },
                { orientation: Orientation.PORTRAIT, x: 143, y: 553 },
            ],
        },
        {
            name: 'Jackpot Ladder Level 5',
            type: AssetType.SPRITE,
            src: 'level_5',
            anchor: 0.5,
            position: [
                { orientation: Orientation.LANDSCAPE, x: 137.25, y: 468 },
                { orientation: Orientation.PORTRAIT, x: 137.25, y: 468 },
            ],
        },
        {
            name: 'Jackpot Ladder Level 6',
            type: AssetType.SPRITE,
            src: 'level_6',
            anchor: 0.5,
            position: [
                { orientation: Orientation.LANDSCAPE, x: 131.5, y: 383 },
                { orientation: Orientation.PORTRAIT, x: 131.5, y: 383 },
            ],
        },
        {
            name: 'Jackpot Ladder Level 7',
            type: AssetType.SPRITE,
            src: 'level_7',
            anchor: 0.5,
            position: [
                { orientation: Orientation.LANDSCAPE, x: 117.75, y: 302 },
                { orientation: Orientation.PORTRAIT, x: 117.75, y: 302 },
            ],
        },
        {
            name: 'Jackpot Ladder Level 8',
            type: AssetType.SPRITE,
            src: 'level_8',
            anchor: 0.5,
            position: [
                { orientation: Orientation.LANDSCAPE, x: 103, y: 218.5 },
                { orientation: Orientation.PORTRAIT, x: 103, y: 218.5 },
            ],
        },
        {
            name: 'Jackpot Ladder Level 9',
            type: AssetType.SPRITE,
            src: 'level_9',
            anchor: 0.5,
            position: [
                { orientation: Orientation.LANDSCAPE, x: 89.25, y: 128 },
                { orientation: Orientation.PORTRAIT, x: 89.25, y: 128 },
            ],
        },
        {
            name: 'Jackpot Ladder Level 10',
            type: AssetType.SPRITE,
            src: 'level_10',
            anchor: 0.5,
            position: [
                { orientation: Orientation.LANDSCAPE, x: 71, y: 43 },
                { orientation: Orientation.PORTRAIT, x: 71, y: 43 },
            ],
        },
        {
            name: JACKPOT_LADDER_SPINE_IDLE_NAME,
            type: AssetType.SPINE,
            visible: true,
            src: 'jackpotLadderAnimation',
            animationSettings: {
                default: {
                    triggerGameSignals: [],
                    animNames: ['ladder_idle'],
                    loops: [true],
                    track: 0,
                    timeScales: [1],
                },
            },
            events: [
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [GameSignals.completeSplashScreenState],
                    callback: function (data: ICustomEventData) {
                        refreshLadderVisualState(data)
                    },
                },
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [
                        GameSignals.restoreFreeSpins,
                        GameSignals.showFreeSpinsRetrigger,
                        GameSignals.updateJarValue,
                        GameSignals.multiplierLanded,
                        (GameSignals as any).holdAndWinStop,
                    ],
                    callback: function (data: ICustomEventData) {
                        refreshLadderVisualState(data)
                    },
                },
            ],
            position: [
                { orientation: Orientation.LANDSCAPE, x: 288.5, y: 924.5 },
                { orientation: Orientation.PORTRAIT, x: 288.5, y: 924.5 },
            ],
            scale: [
                { orientation: Orientation.LANDSCAPE, x: 1, y: 1 },
                { orientation: Orientation.PORTRAIT, x: 1, y: 1 },
            ],
        },
        {
            name: JACKPOT_LADDER_SPINE_ELECTRICITY_NAME,
            type: AssetType.SPINE,
            visible: false,
            src: 'jackpotLadderAnimation',
            animationSettings: {
                default: {
                    triggerGameSignals: [],
                    animNames: ['electricity'],
                    loops: [true],
                    track: 0,
                    timeScales: [1],
                },
            },
            events: [
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [GameSignals.completeSplashScreenState],
                    callback: function (data: ICustomEventData) {
                        refreshLadderVisualState(data)
                    },
                },
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [
                        GameSignals.restoreFreeSpins,
                        GameSignals.showFreeSpinsRetrigger,
                        GameSignals.updateJarValue,
                        GameSignals.multiplierLanded,
                        (GameSignals as any).holdAndWinStop,
                    ],
                    callback: function (data: ICustomEventData) {
                        refreshLadderVisualState(data)
                    },
                },
            ],
            position: [
                { orientation: Orientation.LANDSCAPE, x: 263, y: 927.5 },
                { orientation: Orientation.PORTRAIT, x: 263, y: 927.5 },
            ],
            scale: [
                { orientation: Orientation.LANDSCAPE, x: 0.475, y: 0.95 },
                { orientation: Orientation.PORTRAIT, x: 0.475, y: 0.95 },
            ],
        },
        {
            name: JACKPOT_LADDER_CURRENT_JACKPOT_NAME,
            type: AssetType.SPRITE,
            src: JACKPOT_LADDER_CURRENT_JACKPOT_SRC,
            anchor: 0.5,
            visible: false,
            position: [
                { orientation: Orientation.LANDSCAPE, x: 0, y: -193 },
                { orientation: Orientation.PORTRAIT, x: 0, y: -193 },
            ],
        },
        {
            name: 'Jackpot Ladder Grand Label',
            type: AssetType.SPRITE,
            src: 'grand_text.png',
            anchor: 0.5,
            position: [
                { orientation: Orientation.LANDSCAPE, x: 491, y: 41.5 },
                { orientation: Orientation.PORTRAIT, x: 491, y: 41.5 },
            ],
        },
        {
            name: 'Jackpot Ladder Major Label',
            type: AssetType.SPRITE,
            src: 'major_text.png',
            anchor: 0.5,
            position: [
                { orientation: Orientation.LANDSCAPE, x: 477, y: 124.5 },
                { orientation: Orientation.PORTRAIT, x: 477, y: 124.5 },
            ],
        },
        {
            name: 'Jackpot Ladder Minor Label',
            type: AssetType.SPRITE,
            src: 'minor_text.png',
            anchor: 0.5,
            position: [
                { orientation: Orientation.LANDSCAPE, x: 464, y: 217 },
                { orientation: Orientation.PORTRAIT, x: 464, y: 217 },
            ],
        },
        {
            name: 'Jackpot Ladder Mini Label',
            type: AssetType.SPRITE,
            src: 'mini_text.png',
            anchor: 0.5,
            position: [
                { orientation: Orientation.LANDSCAPE, x: 444, y: 301 },
                { orientation: Orientation.PORTRAIT, x: 444, y: 301 },
            ],
        },
        {
            name: JACKPOT_LADDER_SPINE_COIN_GLOW_NAME,
            type: AssetType.SPINE,
            visible: false,
            src: 'jackpotLadderAnimation',
            animationSettings: {
                default: {
                    triggerGameSignals: [],
                    animNames: ['coin_step3'],
                    loops: [true],
                    track: 0,
                    timeScales: [1],
                },
            },
            events: [
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [
                        GameSignals.restoreFreeSpins,
                        GameSignals.showFreeSpinsRetrigger,
                        GameSignals.updateJarValue,
                        GameSignals.multiplierLanded,
                        (GameSignals as any).holdAndWinStop,
                    ],
                    callback: function (data: ICustomEventData) {
                        refreshLadderVisualState(data)
                    },
                },
            ],
            position: [
                { orientation: Orientation.LANDSCAPE, x: 378, y: 924 },
                { orientation: Orientation.PORTRAIT, x: 378, y: 924 },
            ],
            scale: [
                { orientation: Orientation.LANDSCAPE, x: 0.85, y: 0.85 },
                { orientation: Orientation.PORTRAIT, x: 0.85, y: 0.85 },
            ],
        },
        {
            name: 'Jackpot Ladder Coin 1',
            type: AssetType.SPRITE,
            src: 'coins.png',
            anchor: 0.5,
            position: [
                { orientation: Orientation.LANDSCAPE, x: 337, y: 884 },
                { orientation: Orientation.PORTRAIT, x: 337, y: 884 },
            ],
        },
        {
            name: 'Jackpot Ladder Coin 2',
            type: AssetType.SPRITE,
            src: 'coins.png',
            anchor: 0.5,
            position: [
                { orientation: Orientation.LANDSCAPE, x: 339.5, y: 798.5 },
                { orientation: Orientation.PORTRAIT, x: 339.5, y: 798.5 },
            ],
        },
        {
            name: 'Jackpot Ladder Coin 3',
            type: AssetType.SPRITE,
            src: 'coins.png',
            anchor: 0.5,
            position: [
                { orientation: Orientation.LANDSCAPE, x: 346, y: 712.5 },
                { orientation: Orientation.PORTRAIT, x: 346, y: 712.5 },
            ],
        },
        {
            name: 'Jackpot Ladder Coin 4',
            type: AssetType.SPRITE,
            src: 'coins.png',
            anchor: 0.5,
            position: [
                { orientation: Orientation.LANDSCAPE, x: 346.5, y: 627 },
                { orientation: Orientation.PORTRAIT, x: 346.5, y: 627 },
            ],
        },
        {
            name: 'Jackpot Ladder Coin 5',
            type: AssetType.SPRITE,
            src: 'coins.png',
            anchor: 0.5,
            position: [
                { orientation: Orientation.LANDSCAPE, x: 350.5, y: 544 },
                { orientation: Orientation.PORTRAIT, x: 350.5, y: 544 },
            ],
        },
        {
            name: 'Jackpot Ladder Coin 6',
            type: AssetType.SPRITE,
            src: 'coins.png',
            anchor: 0.5,
            position: [
                { orientation: Orientation.LANDSCAPE, x: 354.5, y: 461 },
                { orientation: Orientation.PORTRAIT, x: 354.5, y: 461 },
            ],
        },
        {
            name: 'Jackpot Ladder Coin 7',
            type: AssetType.SPRITE,
            src: 'coins.png',
            anchor: 0.5,
            position: [
                { orientation: Orientation.LANDSCAPE, x: 360.5, y: 375.5 },
                { orientation: Orientation.PORTRAIT, x: 360.5, y: 375.5 },
            ],
        },
        {
            name: 'Jackpot Ladder Coin 8',
            type: AssetType.SPRITE,
            src: 'coins.png',
            anchor: 0.5,
            position: [
                { orientation: Orientation.LANDSCAPE, x: 369, y: 294.5 },
                { orientation: Orientation.PORTRAIT, x: 369, y: 294.5 },
            ],
        },
        {
            name: 'Jackpot Ladder Coin 9',
            type: AssetType.SPRITE,
            src: 'coins.png',
            anchor: 0.5,
            position: [
                { orientation: Orientation.LANDSCAPE, x: 386.5, y: 210 },
                { orientation: Orientation.PORTRAIT, x: 386.5, y: 210 },
            ],
        },
        {
            name: 'Jackpot Ladder Coin 10',
            type: AssetType.SPRITE,
            src: 'coins.png',
            anchor: 0.5,
            position: [
                { orientation: Orientation.LANDSCAPE, x: 399, y: 122 },
                { orientation: Orientation.PORTRAIT, x: 399, y: 122 },
            ],
        },
        {
            name: 'Jackpot Ladder Coin 11',
            type: AssetType.SPRITE,
            src: 'coins.png',
            anchor: 0.5,
            position: [
                { orientation: Orientation.LANDSCAPE, x: 413, y: 32.5 },
                { orientation: Orientation.PORTRAIT, x: 413, y: 32.5 },
            ],
        },
        {
            name: JACKPOT_LADDER_SPINE_COIN_STEPS_NAME,
            type: AssetType.SPINE,
            visible: false,
            src: 'jackpotLadderAnimation',
            animationSettings: {
                default: {
                    triggerGameSignals: [],
                    animNames: ['coin_step3'],
                    loops: [true],
                    track: 0,
                    timeScales: [1],
                },
            },
            events: [
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [
                        GameSignals.restoreFreeSpins,
                        GameSignals.showFreeSpinsRetrigger,
                        GameSignals.updateJarValue,
                        GameSignals.multiplierLanded,
                        (GameSignals as any).holdAndWinStop,
                    ],
                    callback: function (data: ICustomEventData) {
                        refreshLadderVisualState(data)
                    },
                },
            ],
            position: [
                { orientation: Orientation.LANDSCAPE, x: 378, y: 924 },
                { orientation: Orientation.PORTRAIT, x: 378, y: 924 },
            ],
            scale: [
                { orientation: Orientation.LANDSCAPE, x: 0.85, y: 0.85 },
                { orientation: Orientation.PORTRAIT, x: 0.85, y: 0.85 },
            ],
        },
        {
            name: JACKPOT_LADDER_SPINE_COIN_STEPS_NAME,
            type: AssetType.SPINE,
            visible: false,
            src: 'jackpotLadderAnimation',
            animationSettings: {
                default: {
                    triggerGameSignals: [],
                    animNames: ['coin_step1', 'coin_step2'],
                    loops: [false, true],
                    track: 0,
                    timeScales: [1, 1],
                },
            },
            events: [
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [GameSignals.completeSplashScreenState],
                    callback: function (data: ICustomEventData) {
                        refreshLadderVisualState(data)
                    },
                },
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [
                        GameSignals.restoreFreeSpins,
                        GameSignals.showFreeSpinsRetrigger,
                        GameSignals.updateJarValue,
                        GameSignals.multiplierLanded,
                        (GameSignals as any).holdAndWinStop,
                    ],
                    callback: function (data: ICustomEventData) {
                        refreshLadderVisualState(data)
                    },
                },
            ],
            position: [
                { orientation: Orientation.LANDSCAPE, x: 378, y: 924 },
                { orientation: Orientation.PORTRAIT, x: 378, y: 924 },
            ],
            scale: [
                { orientation: Orientation.LANDSCAPE, x: 0.85, y: 0.85 },
                { orientation: Orientation.PORTRAIT, x: 0.85, y: 0.85 },
            ],
        },
        ...JACKPOT_LADDER_STEP_VALUE_POSITIONS.slice(0, JACKPOT_LADDER_COINS_COUNT).map((position, index) =>
            createStepPrizeValueItem(index + 1, position.x, position.y)
        ),
        ...JACKPOT_LADDER_COIN_TEXT_POSITIONS.slice(0, JACKPOT_LADDER_COINS_COUNT).map((position, index) =>
            createCoinValueItem(index + 1, position.x, position.y)
        ),
    ],
}

//## ---------------------------------------------------------------------------------------
//## Read-only accessors added for the ticker milestone messages. These only forward to the
//## existing ladder helpers so both read the exact same values; nothing above is modified.
//## ---------------------------------------------------------------------------------------

//## Coins still needed per level, resolved through the ladder's own source precedence.
export const readLadderMilestones = (signalData?: any): number[] => getLadderMilestones(signalData)

//## Index of the level currently being worked toward, or -1 when every level is cleared.
export const readLadderTargetLevelIndex = (milestones: number[]): number =>
    getCurrentTargetLevelIndex(milestones)

//## Level the ladder was working toward last time a milestone check ran.
let lastReachedTargetIndex: number | null = null

const resetLadderSessionState = (): void => {
    setRuntimeLadderMilestones([])
    setRuntimeLadderCount(-1)
    lastReachedTargetIndex = null
}

/**
 * Establishes the baseline the first collect of a session compares against.
 *
 * Without this the tracker is still null when the first collect finishes, so a milestone
 * reached on the very first free spin is never detected. It also has to run per session:
 * carrying the previous session's index over would leave the new one comparing against a
 * higher level and suppressing every upgrade until it climbed past it.
 */
export const seedLadderMilestoneTracking = (signalData?: any): void => {
    lastReachedTargetIndex = getCurrentTargetLevelIndex(getLadderMilestones(signalData))
}

/**
 * Coins the ladder can still take before every level is unlocked.
 *
 * Read before the collect flies start, so it reports the state the ladder is coming from:
 * while the server count runs ahead of the runtime one the milestones resolve to the
 * pre-spin values. Coins landing beyond this capacity are worth 0 and are never collected.
 */
export const getLadderRemainingCoinCapacity = (signalData?: any): number =>
    getLadderMilestones(signalData).reduce((total, value) => {
        const remaining = Number(value)
        return total + (Number.isFinite(remaining) && remaining > 0 ? remaining : 0)
    }, 0)

/**
 * Index of the ladder level that was just completed, or null when no milestone was crossed.
 *
 * The milestone array is issued per spin by the server and is not decremented coin by coin,
 * so a crossing is only observable by comparing the target level against the previous check.
 * Reading consumes the transition, so exactly one caller may drive this — currently
 * FsCoinCollectState. TickerMessageSignalsManager still runs its own parallel comparison;
 * both should eventually share this one.
 */
export const resolveReachedLadderMilestone = (signalData?: any): number | null => {
    const milestones = getLadderMilestones(signalData)
    const targetIndex = getCurrentTargetLevelIndex(milestones)
    const previousIndex = lastReachedTargetIndex

    lastReachedTargetIndex = targetIndex

    //## Nothing to compare against on the first check of a session.
    if (previousIndex === null || previousIndex < 0) {
        return null
    }

    //## -1 means every level is cleared, so the last one is the milestone just reached.
    if (targetIndex < 0) {
        return milestones.length > 0 ? milestones.length - 1 : null
    }

    return targetIndex > previousIndex ? targetIndex - 1 : null
}

//## The prize a ladder level pays at the current bet, formatted exactly as the ladder prints it.
export const readLadderStepPrizeText = (stepIndex: number): string => {
    const totalBet = getCurrentTotalBet()
    const multiplier = Number(JACKPOT_LADDER_STEP_PRIZE_MULTIPLIERS[stepIndex] ?? 0)

    return formatStepPrizeValue(totalBet * multiplier)
}

export type LadderJackpotTier = 'mini' | 'minor' | 'major' | 'grand'

//## Get The Ladder Jackpot Index..!
export const readLadderCurrentJackpotIndex = (signalData?: any): number => {
    const milestones = getLadderMilestones(signalData)
    const targetIndex = getCurrentTargetLevelIndex(milestones)

    //## -1 means every level is cleared, so the marker rests on the last one.
    if (targetIndex < 0) {
        return milestones.length > 0 ? milestones.length - 1 : -1
    }

    return targetIndex - 1
}

//## Get The Ladder Jackpot Prize Value..!
export const readLadderJackpotPrizeText = (tier: LadderJackpotTier): string => {
    const index = Object.keys(JACKPOT_LADDER_LEVEL_NAMES)
        .map(Number)
        .find((key) => String(JACKPOT_LADDER_LEVEL_NAMES[key]).toLowerCase() === tier)

    return index == null ? '' : readLadderStepPrizeText(index)
}

//## Get The Ladder Current Jackpot..!
export const readLadderCurrentJackpot = (
    signalData?: any
): { tier: LadderJackpotTier; index: number; prizeText: string } | null => {
    const index = readLadderCurrentJackpotIndex(signalData)
    const levelName = JACKPOT_LADDER_LEVEL_NAMES[index]

    if (!levelName) {
        return null
    }

    return {
        tier: levelName.toLowerCase() as LadderJackpotTier,
        index,
        prizeText: readLadderStepPrizeText(index),
    }
}
